import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { Avatar } from '../../components/ui/Avatar';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton, SkeletonKPIRow, SkeletonTable } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
// Telefono: i filtri finiscono dietro un solo tasto. Sul computer questo
// componente restituisce i suoi figli così come sono, quindi qui non cambia niente.
import { MobileFilters } from '../../components/ui/MobileFilters';
// Quello che dal telefono non si fa (qui: la bacheca) diventa una riga onesta.
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { useUrlState } from '../../hooks/useUrlState';
import { useKanbanDnd } from '../../lib/dnd';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  dealsSummaryQuery, dealsListQuery, dealOwnersQuery, dealFasceQuery, pipelinesQuery,
  SCENARI, SCENARIO_SHORT, SPESA, VIEWS, canWriteDeals, contactLine, counterparty, dueInfo, exitStages, fullName, pathStages,
  type DealItem, type DealListResponse, type DealView, type Pipeline, type ScenarioKey, type StageLite,
} from './dealsApi';
import { useDealActions } from './useDealActions';
import { ClosePopover } from './ClosePopover';
import { MovePipelinePopover } from './MovePipelinePopover';
import { NextActionPopover } from './NextActionPopover';
import './deals.css';

/**
 * Trattative — quattro processi commerciali diversi, ognuno coi suoi step.
 *   page-ed-hd  → titolo + sottotitolo + "Nuova trattativa"
 *   stat-band   → i tre periodi chiesti da Andrea (valore lordo + pesato) e
 *                 quante trattative sono ferme, sulla pipeline scelta
 *   pill        → In corso | Vinte | Chiuse | Tutte, calcolate sull'esito degli step
 *   vista       → Lista oppure Kanban con le colonne DELLA pipeline scelta più
 *                 "Chiuse" in fondo; la scelta resta in localStorage
 *   quick add   → azienda + step + configurazione (o importo), Invio crea
 *
 * Il selettore di pipeline è il primo controllo: "Tutte le pipeline" dà il
 * totale che interessa ad Andrea, una pipeline sola accende la bacheca (le
 * colonne sono i suoi step, e mescolarle non avrebbe senso).
 *
 * Sul valore: nella pipeline del voucher non si digita un importo. Si sceglie
 * la configurazione (20k o 40k) e il ricavo per Masamune lo calcola il server
 * (1.200 di istruttoria + 6% del contributo + 120 €/mese di canone). Altrove
 * vale l'importo libero.
 *
 * Stato in URL: pipeline · view · q · owner · fascia · from · to · stale
 *   es. /trattative?pipeline=voucher · /trattative?from=2026-09-01&to=2026-10-31
 */

const URL_DEFAULTS: Record<string, string> = { pipeline: '', view: 'aperte', q: '', owner: '', fascia: '', from: '', to: '', stale: '' };
const PAGE_SIZE = 25;
const LAYOUT_KEY = 'deals-view';
const PIPELINE_KEY = 'deals-pipeline';

export function DealsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const [, setSearchParams] = useSearchParams();
  const canWrite = canWriteDeals(user);

  const { data: pipelines } = useQuery(pipelinesQuery);
  const [pipelineParam, setPipeline] = useUrlState<string>('pipeline', '');
  const [viewParam] = useUrlState<string>('view', 'aperte');
  const view: DealView = (VIEWS.some((v) => v.key === viewParam) ? viewParam : 'aperte') as DealView;
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [owner, setOwner] = useUrlState<string>('owner', '');
  const [fascia, setFascia] = useUrlState<string>('fascia', '');
  const [fromParam] = useUrlState<string>('from', '');
  const [toParam] = useUrlState<string>('to', '');
  const [staleParam] = useUrlState<string>('stale', '');
  const stale = staleParam === '1';

  // L'ultima pipeline guardata torna da sola alla visita successiva
  useEffect(() => {
    if (pipelineParam) { localStorage.setItem(PIPELINE_KEY, pipelineParam); return; }
    const saved = localStorage.getItem(PIPELINE_KEY);
    if (saved && (pipelines ?? []).some((p) => p.slug === saved)) setPipeline(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelines]);

  const pipeline: Pipeline | undefined = useMemo(
    () => (pipelines ?? []).find((p) => p.slug === pipelineParam),
    [pipelines, pipelineParam],
  );

  // Vista persistita; la bacheca vuole una pipeline sola (le colonne sono i suoi step)
  const [layoutPref, setLayoutPref] = useState<'list' | 'kanban'>(() => (localStorage.getItem(LAYOUT_KEY) === 'kanban' ? 'kanban' : 'list'));
  const setLayout = (v: 'list' | 'kanban') => { setLayoutPref(v); localStorage.setItem(LAYOUT_KEY, v); };
  const layout: 'list' | 'kanban' = pipeline ? layoutPref : 'list';

  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [pipelineParam, view, search, owner, fascia, fromParam, toParam, stale]);

  const setParamsBatch = (next: Partial<Record<keyof typeof URL_DEFAULTS, string>>) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(next)) {
        if (v == null || v === URL_DEFAULTS[k]) sp.delete(k);
        else sp.set(k, v);
      }
      return sp;
    });
  };
  const clearFilters = () => setParamsBatch({ q: '', owner: '', fascia: '', from: '', to: '', stale: '' });
  const hasFilters = !!(search.trim() || owner || fascia || fromParam || toParam || stale);

  const { data: summary, isLoading: loadingSummary } = useQuery(dealsSummaryQuery(pipelineParam || undefined));
  const { data: owners } = useQuery(dealOwnersQuery);
  const { data: fasce } = useQuery(dealFasceQuery);

  const baseParams = useMemo(() => ({
    view,
    pipeline: pipelineParam || undefined,
    ownerId: owner || undefined,
    fascia: fascia || undefined,
    search: search.trim() || undefined,
    from: fromParam || undefined,
    to: toParam || undefined,
    stale: stale || undefined,
  }), [view, pipelineParam, owner, fascia, search, fromParam, toParam, stale]);

  const listParams = useMemo(() => ({ ...baseParams, page, pageSize: PAGE_SIZE }), [baseParams, page]);
  // Kanban: tutti gli step della pipeline in una pagina sola e larga
  const kanbanParams = useMemo(() => ({ ...baseParams, view: 'tutte' as DealView, page: 1, pageSize: 500 }), [baseParams]);

  const listQ = useQuery({ ...dealsListQuery(listParams), enabled: layout === 'list' });
  const kanbanQ = useQuery({ ...dealsListQuery(kanbanParams), enabled: layout === 'kanban' });

  /** Conteggi delle pill: sull'esito degli step, non su nomi cablati. */
  const countFor = (v: DealView): number | undefined => {
    if (!summary) return undefined;
    const of = (outcome: string) => summary.byStage.filter((s) => s.outcome === outcome).reduce((n, s) => n + s.count, 0);
    if (v === 'vinte') return of('VINTO');
    if (v === 'chiuse') return of('PERSO');
    if (v === 'tutte') return summary.byStage.reduce((n, s) => n + s.count, 0);
    return of('APERTO');
  };

  const periodoAttivo = summary?.byPeriod.find((p) => p.from === fromParam && p.to === toParam);

  return (
    <div className="page">
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div>
          <h1 className="ed-title">Trattative</h1>
          <div className="ed-sub">A che punto è ogni pratica, quanto vale e chi deve chiamare chi.</div>
        </div>
        {/* d-only: sul telefono l'azione principale non sta in cima, dove il
            pollice non arriva, ma nella barra in fondo alla pagina. */}
        <div className="flex gap-2 pb-1 items-center d-only">
          {canWrite && (
            <button className="btn btn-primary" onClick={() => openModal('newDeal', pipeline ? { pipeline: pipeline.slug } : undefined)}>
              <Icon name="plus" /> Nuova trattativa
            </button>
          )}
        </div>
      </div>

      {loadingSummary && !summary ? (
        <SkeletonKPIRow count={4} withIcon={false} />
      ) : (
        <div className="stat-band">
          {(summary?.byPeriod ?? []).map((p) => (
            <div
              key={p.key}
              className="st-click"
              role="button"
              tabIndex={0}
              onClick={() => setParamsBatch({ from: p.from, to: p.to, stale: '' })}
              onKeyDown={(e) => { if (e.key === 'Enter') setParamsBatch({ from: p.from, to: p.to, stale: '' }); }}
            >
              <div className="st-label">{p.label}</div>
              <div className="st-value"><span className="u">€</span><AnimatedNumber value={p.amount} format={fmtEur} /></div>
              <div className="st-delta">pesato € {fmtEur(p.weighted)}</div>
            </div>
          ))}
          <div
            className="st-click"
            role="button"
            tabIndex={0}
            onClick={() => setParamsBatch({ stale: stale ? '' : '1', from: '', to: '' })}
            onKeyDown={(e) => { if (e.key === 'Enter') setParamsBatch({ stale: stale ? '' : '1', from: '', to: '' }); }}
          >
            <div className="st-label">Ferme da oltre {summary?.staleDays ?? 14} giorni</div>
            <div className="st-value"><AnimatedNumber value={summary?.stale ?? 0} /></div>
            <div className={`st-delta${(summary?.stale ?? 0) > 0 ? ' text-alert' : ''}`}>
              {(summary?.stale ?? 0) > 0 ? 'da richiamare' : 'nessuna'}
            </div>
          </div>
        </div>
      )}

      {/* Quello che resta fuori dai conti, detto in una riga sola */}
      {summary && (summary.missingScenario > 0 || summary.undated.count > 0) && (
        <div className="adm-note">
          <Icon name="help" size={12} className="mute" />
          <span>
            {summary.missingScenario > 0 && (
              <>Fuori dai totali: <strong>{summary.missingScenario}</strong> voucher senza configurazione scelta{summary.undated.count > 0 ? ', ' : '. '}</>
            )}
            {summary.undated.count > 0 && (
              <><strong>{summary.undated.count}</strong> senza data prevista, quindi in nessun periodo.</>
            )}
          </span>
        </div>
      )}

      {/* In kanban gli step sono già le colonne: le pill servono solo alla lista */}
      {layout === 'list' && (
        <div className="page-tabs">
          {VIEWS.map((v) => (
            <div key={v.key} className="page-tab" aria-selected={view === v.key} onClick={() => setParamsBatch({ view: v.key })}>
              {v.label}
              {countFor(v.key) != null && <span className="c">{countFor(v.key)}</span>}
            </div>
          ))}
        </div>
      )}

      <MobileFilters attivi={[search.trim(), owner, fascia, fromParam, stale].filter(Boolean).length}>
      <div className={`filterbar filterbar--float mb-4 flex-wrap${layout === 'kanban' ? ' mt-4' : ''}`}>
        {/* Il selettore di pipeline: è il primo controllo, col nome per esteso.
            inp-sm al posto dell'altezza scritta a mano: è la classe del campo
            compatto del progetto (--h-base, 32px, 12px di testo), cioè la stessa
            misura che c'era prima ma presa dal token invece che da uno style
            inline. Serve anche sul telefono: uno style inline batte anche
            mobile.css, e i 32px restavano 32 dove devono essere 44. */}
        <select
          className="inp inp-sm font-semibold"
          style={{ width: 250 }}
          value={pipelineParam}
          onChange={(e) => setParamsBatch({ pipeline: e.target.value, from: '', to: '' })}
          aria-label="Pipeline"
        >
          <option value="">Tutte le pipeline</option>
          {(pipelines ?? []).map((p) => <option key={p.id} value={p.slug}>{p.name}</option>)}
        </select>

        {/* La porta per crearle e cambiarle sta accanto alla tendina: il bisogno
            ("qui manca un passaggio") nasce sempre guardando le trattative, non
            girando per le impostazioni. Si parte dalla pipeline scelta.
            L'altezza è già quella di .btn-sm (--h-base): non serve scriverla. */}
        <button
          className="btn btn-sm btn-ghost"
          title="Crea o modifica le pipeline e i loro passaggi"
          onClick={() => navigate(`/trattative/pipeline${pipelineParam ? `?p=${encodeURIComponent(pipelineParam)}` : ''}`)}
        >
          <Icon name="settings" size={12} /> Gestisci
        </button>

        <div className="tweak-seg" role="group" aria-label="Vista">
          <button aria-pressed={layout === 'list'} onClick={() => setLayout('list')}><Icon name="list" size={12} /> Lista</button>
          <button
            aria-pressed={layout === 'kanban'}
            disabled={!pipeline}
            title={pipeline ? undefined : 'Scegli una pipeline: le colonne sono i suoi step'}
            onClick={() => setLayout('kanban')}
          >
            <Icon name="kanban" size={12} /> Kanban
          </button>
        </div>

        <div className="divider" />

        <div className="tb-search" style={{ flex: '1 1 150px', minWidth: 150, maxWidth: 300, margin: 0 }}>
          <Icon name="search" size={12} />
          <input aria-label="Cerca trattative" placeholder="Cerca azienda, persona, telefono…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <select className="inp inp-sm" style={{ width: 150 }} value={owner} onChange={(e) => setOwner(e.target.value)} aria-label="Chi segue">
          <option value="">Chi segue</option>
          {(owners ?? []).map((u) => <option key={u.id} value={u.id}>{fullName(u)}</option>)}
        </select>
        <select className="inp inp-sm" style={{ width: 105 }} value={fascia} onChange={(e) => setFascia(e.target.value)} aria-label="Fascia">
          <option value="">Fascia</option>
          {(fasce ?? []).map((f) => <option key={f} value={f}>Fascia {f}</option>)}
        </select>

        {periodoAttivo && (
          <Chip tone="active" onClick={() => setParamsBatch({ from: '', to: '' })}>
            {periodoAttivo.label} <Icon name="x" size={10} />
          </Chip>
        )}
        {stale && (
          <Chip tone="active" onClick={() => setParamsBatch({ stale: '' })}>
            Ferme <Icon name="x" size={10} />
          </Chip>
        )}

        {/* Il conteggio sta già nella pill attiva: qui il valore, che è l'altra domanda */}
        <span className="mute text-[12px]">
          {layout === 'list'
            ? `€ ${fmtEur(listQ.data?.totals.amount ?? 0)} in lista`
            : `${kanbanQ.data?.total ?? 0} in bacheca`}
        </span>
      </div>
      </MobileFilters>

      {layout === 'list' ? (
        <>
          {canWrite && pipeline && <QuickAdd pipeline={pipeline} />}
          <ListView
            query={listQ}
            page={page}
            onPage={setPage}
            hasFilters={hasFilters}
            onClearFilters={clearFilters}
            canWrite={canWrite}
            showPipeline={!pipeline}
            onNew={() => openModal('newDeal', pipeline ? { pipeline: pipeline.slug } : undefined)}
            onOpen={(id) => navigate(`/trattative/${id}`)}
          />
        </>
      ) : (
        // La bacheca sono colonne affiancate che si trascinano: su uno schermo
        // stretto non è una vista, è un imbuto. Sul telefono al suo posto
        // compare una riga che lo dice; sul computer non cambia niente.
        <DesktopOnly cosa="La bacheca" perche="sono colonne da guardare una accanto all'altra, e le trattative si spostano trascinandole. Dal telefono resta la lista, che fa le stesse cose.">
        <KanbanView
          pipeline={pipeline!}
          queryKey={dealsListQuery(kanbanParams).queryKey}
          isLoading={kanbanQ.isLoading && !kanbanQ.data}
          items={kanbanQ.data?.items ?? []}
          canWrite={canWrite}
          hasFilters={hasFilters}
          onClearFilters={clearFilters}
          onOpen={(id) => navigate(`/trattative/${id}`)}
        />
        </DesktopOnly>
      )}

      {/* Telefono: l'azione principale della schermata, appoggiata sopra la
          barra di navigazione. m-only la spegne sul computer, dove la stessa
          azione sta già in cima. */}
      {canWrite && (
        <DesktopOnly
          cosa="L'apertura di una nuova trattativa"
          perche="richiede dati commerciali, valore e scadenze; dal telefono puoi spostare e chiudere quelle già aperte."
        >
          <div className="m-actionbar m-only">
            <button className="btn btn-primary" onClick={() => openModal('newDeal', pipeline ? { pipeline: pipeline.slug } : undefined)}>
              <Icon name="plus" /> Nuova trattativa
            </button>
          </div>
        </DesktopOnly>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Pezzi riusati: fascia, requisiti, valore
// ════════════════════════════════════════════════════════════════════

/** Fascia A/B/C/D: è l'ordine con cui si chiama. Pallino pieno = priorità alta. */
function FasciaDot({ fascia, withLetter }: { fascia: string | null; withLetter?: boolean }) {
  if (!fascia) return null;
  const opacity = { A: 1, B: 0.7, C: 0.45, D: 0.3 }[fascia.toUpperCase()] ?? 0.5;
  return (
    <span className="inline-flex items-center gap-1 text-[12px] mute shrink-0" title={`Fascia ${fascia}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--text)', opacity }} />
      {withLetter && fascia}
    </span>
  );
}

/**
 * La palette è monocromatica: il "positivo" è inchiostro (bianco in dark), il
 * rosso è l'unica eccezione e vuol dire "manca". `var(--ok)` qui non si usa:
 * vale #1a1a1a anche in dark, cioè invisibile sulle card scure.
 */
const REQ_COLOR: Record<string, string> = { OK: 'var(--text)', MANCA: 'var(--alert)', DA_VERIFICARE: 'var(--line-2)' };

/** I tre requisiti del bando in tre pallini: connettività, polizza, DURC. */
function ReqDots({ d }: { d: Pick<DealItem, 'reqConnettivita' | 'reqPolizza' | 'reqDurc' | 'requisitiMancanti'> }) {
  const items: Array<[string, string]> = [
    ['Connettività', d.reqConnettivita],
    ['Polizza', d.reqPolizza],
    ['DURC', d.reqDurc],
  ];
  const title = d.requisitiMancanti.length ? `Da sistemare: ${d.requisitiMancanti.join(', ')}` : 'Requisiti a posto';
  return (
    <span className="inline-flex items-center gap-1 shrink-0" title={title}>
      {items.map(([label, status]) => (
        <span key={label} className="w-2 h-2 rounded-full" style={{ background: REQ_COLOR[status] }} aria-label={`${label}: ${status}`} />
      ))}
    </span>
  );
}

/** Valore della riga: sul voucher è calcolato, altrove è l'importo scritto. */
/**
 * Il valore è l'imponibile che fatturiamo noi, voucher compreso. Un progetto
 * può non essere né 20k né 40k (La Rosa 30.000, Villa Fago 22.000): finché c'è
 * l'importo il valore si mostra, la configurazione è solo una scorciatoia.
 */
function Valore({ d }: { d: DealItem }) {
  if (!d.value) {
    return <span className="mute-2" title="Scrivi l'imponibile del progetto per sapere quanto vale">da definire</span>;
  }
  return <>€ {fmtEur(d.value, true)}</>;
}

// ════════════════════════════════════════════════════════════════════
// Quick add: azienda + step + configurazione (o importo) · Invio crea
// ════════════════════════════════════════════════════════════════════
function QuickAdd({ pipeline }: { pipeline: Pipeline }) {
  const { create } = useDealActions();
  const stages = pathStages(pipeline);
  const [name, setName] = useState('');
  const [stageId, setStageId] = useState(stages[0]?.id ?? '');
  const [scenario, setScenario] = useState('');
  const [amount, setAmount] = useState('');
  const isVoucher = pipeline.kind === 'VOUCHER';
  useEffect(() => { setStageId(stages[0]?.id ?? ''); }, [pipeline.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => {
    const n = name.trim();
    if (!n || create.isPending) return;
    create.mutate({
      title: n,
      companyName: n,
      pipeline: pipeline.slug,
      stageId,
      scenario: isVoucher ? scenario || null : null,
      // Anche sul voucher vale l'imponibile: la configurazione 20k/40k è solo
      // la scorciatoia che lo compila, e un progetto può valerne 30.000
      amount: Number(amount.replace(',', '.')) || 0,
    });
    setName(''); // step e configurazione restano: salva-e-aggiungi-un-altro
  };

  return (
    <form className="tk-quick" onSubmit={(e) => { e.preventDefault(); submit(); }} aria-label="Aggiungi una trattativa al volo">
      <div className="tk-quick-title">
        <Icon name="plus" size={12} className="mute" />
        <input
          className="inp"
          placeholder="Nuova trattativa: scrivi l'azienda e premi Invio"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') setName(''); }}
          aria-label="Azienda"
        />
      </div>
      <select className="inp" value={stageId} onChange={(e) => setStageId(e.target.value)} aria-label="Step">
        {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      {isVoucher && (
        <select
          className="inp"
          value={scenario}
          onChange={(e) => {
            const s = e.target.value;
            setScenario(s);
            if (s) setAmount(String(SPESA[s as ScenarioKey])); // la taglia compila l'imponibile
          }}
          aria-label="Configurazione"
        >
          <option value="">Su misura</option>
          {SCENARI.map((s) => <option key={s} value={s}>{SCENARIO_SHORT[s]}</option>)}
        </select>
      )}
      <input className="inp mono" inputMode="decimal" placeholder="Imponibile €" value={amount} onChange={(e) => setAmount(e.target.value)} aria-label="Imponibile" />
      <button className="btn btn-primary" type="submit" disabled={!name.trim() || create.isPending}>Aggiungi</button>
    </form>
  );
}

// ════════════════════════════════════════════════════════════════════
// Lista
// ════════════════════════════════════════════════════════════════════
function ListView({ query, page, onPage, hasFilters, onClearFilters, canWrite, showPipeline, onNew, onOpen }: {
  query: { data?: DealListResponse; isLoading: boolean };
  page: number;
  onPage: (p: number) => void;
  hasFilters: boolean;
  onClearFilters: () => void;
  canWrite: boolean;
  /** Con "Tutte le pipeline" serve una colonna che dica di quale si tratta. */
  showPipeline: boolean;
  onNew: () => void;
  onOpen: (id: string) => void;
}) {
  const items = query.data?.items ?? [];

  if (query.isLoading && !query.data) {
    return <div className="ed-section"><table className="tbl tbl--ed tbl--card"><tbody><SkeletonTable rows={6} cols={6} withAvatar={false} /></tbody></table></div>;
  }

  if (!items.length) {
    if (hasFilters) {
      return (
        <EmptyState
          brand
          title="Nessuna trattativa con questi filtri"
          description="Azzera i filtri per vedere le altre."
          action={<button className="btn" onClick={onClearFilters}>Azzera i filtri</button>}
        />
      );
    }
    return (
      <EmptyState
        brand
        title="Nessuna trattativa"
        description="Scegli una pipeline e scrivi il nome della prima azienda da contattare."
        action={canWrite ? <button className="btn btn-primary d-only" onClick={onNew}><Icon name="plus" size={12} /> Nuova trattativa</button> : undefined}
      />
    );
  }

  return (
    <div className="ed-section">
      {/* tbl--card: sotto i 768px ogni riga diventa una scheda. Sul computer
          non fa niente, la tabella resta una tabella. Le celle dicono con
          data-m che ruolo hanno nella scheda e con data-l come si chiamano
          (l'intestazione di colonna lì non c'è più). Regole: os-planning/mobile.md */}
      <table className="tbl tbl--ed tbl--card">
        <thead>
          {/* Le larghezze sono state strette finché la tabella non è tornata dentro
              il foglio: a 1440 misurava 1335px in un contenitore da 1064 e le
              ultime due colonne finivano fuori, con una barra di scorrimento
              orizzontale su una lista. Step e Prossima azione erano dichiarate
              più larghe di quanto il loro contenuto chiedesse. */}
          <tr>
            <th>Trattativa</th>
            {showPipeline && <th style={{ width: 132 }}>Pipeline</th>}
            <th style={{ width: 176 }}>Step</th>
            {/* I requisiti sono del bando: con le pipeline mescolate non dicono niente */}
            {!showPipeline && <th style={{ width: 80 }}>Requisiti</th>}
            <th className="num" style={{ width: 120 }}>Valore</th>
            <th style={{ width: 110 }}>Prevista</th>
            <th style={{ width: 180 }}>Prossima azione</th>
            <th style={{ width: 60 }}>Chi</th>
            <th style={{ width: 52 }} />
          </tr>
        </thead>
        <tbody>
          {items.map((d) => <Row key={d.id} d={d} canWrite={canWrite} showPipeline={showPipeline} onOpen={onOpen} />)}
        </tbody>
      </table>
      {query.data && <Pagination page={page} pageSize={query.data.pageSize} total={query.data.total} totalPages={query.data.totalPages} onPage={onPage} />}
    </div>
  );
}

function Row({ d, canWrite, showPipeline, onOpen }: { d: DealItem; canWrite: boolean; showPipeline: boolean; onOpen: (id: string) => void }) {
  const { data: pipelines } = useQuery(pipelinesQuery);
  const { win, close, movePipeline, nextAction } = useDealActions();
  const [closing, setClosing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [planning, setPlanning] = useState(false);
  const azienda = counterparty(d);
  const sotto = contactLine(d);
  const prevista = dueInfo(d.expectedCloseDate, !d.isOpen);
  const azione = d.nextTask ? dueInfo(d.nextTask.dueDate) : null;

  const full = (pipelines ?? []).find((p) => p.id === d.pipelineId);
  const wonStage = pathStages(full).find((s) => s.outcome === 'VINTO');
  const lostStages = exitStages(full);

  return (
    <tr onClick={() => onOpen(d.id)}>
      <td data-m="hd">
        {/* flex-wrap: i chip non si tagliano, quindi senza il "vai a capo" la
            loro larghezza intera diventava la larghezza minima della colonna, e
            una riga con "Rinnovo" più "Estendibile a 40k" da sola spingeva
            fuori tutta la tabella. Con la larghezza che c'è a 1440 non va a capo
            nessuno: è una valvola, non un cambio di disegno. */}
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className="font-medium truncate deal-name">{azienda}</span>
          <FasciaDot fascia={d.fascia} withLetter />
          {d.isRinnovo && <Chip tone="ghost">Rinnovo</Chip>}
          {d.estendibile && <Chip tone="info">Estendibile a 40k</Chip>}
        </div>
        <div className="text-[12px] mute mt-0.5">
          {[
            sotto,
            d.scenario ? SCENARIO_SHORT[d.scenario] : null,
            // Un rinnovo non è denaro nuovo: qui si legge quanto vale oggi il
            // contratto che scade, che è quello che si rischia di perdere
            d.renewalOf ? `oggi € ${fmtEur(d.renewalOf.annuo, true)}/anno` : null,
          ].filter(Boolean).join(' · ') || '—'}
        </div>
      </td>
      {/* Il nome per esteso sta nel title: in colonna si taglia, non si accavalla */}
      {/* 108 e non 160: con i 20px di padding per lato la cella da 160 chiedeva
          200px a una colonna dichiarata 170, e la differenza usciva dalla tabella.
          Questa colonna esiste solo con "Tutte le pipeline", cioè quando serve
          a distinguere una riga dall'altra: il nome per esteso sta nel title. */}
      {showPipeline && <td data-m="key" data-l="Pipeline"><div className="mute text-[13px] truncate" style={{ width: 92 }} title={d.pipeline.name}>{d.pipeline.name}</div></td>}
      {/* Niente nowrap qui: il nome di uno step lo scrive chi configura la
          pipeline e può essere lungo, e con il nowrap la sua larghezza intera
          diventava la larghezza minima della colonna. Alla larghezza che la
          colonna ha davvero non va a capo nessuno. */}
      <td data-m="key" data-l="Step">
        <Chip tone={d.isClosed ? 'ghost' : d.isWon ? 'ok' : 'info'} dot>{d.stage.name}</Chip>
      </td>
      {/* I pallini dei requisiti sono un riassunto a colpo d'occhio che sul
          telefono non si legge senza legenda: si aprono nel dettaglio. */}
      {!showPipeline && <td data-m="off">{d.isVoucher ? <ReqDots d={d} /> : <span className="mute-2">—</span>}</td>}
      <td data-m="key" data-l="Valore" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}><Valore d={d} /></td>
      <td data-m="key" data-l="Prevista" className="mono text-[13px]" style={{ whiteSpace: 'nowrap' }}>
        <span className={prevista.tone === 'alert' ? 'text-alert font-semibold' : prevista.tone === 'warn' ? 'text-warn' : 'mute'}>{prevista.label}</span>
      </td>
      {/* Riga intera: è la risposta alla domanda per cui si apre questa pagina
          dal telefono, "chi devo chiamare e quando". */}
      <td data-m="row" data-l="Prossima azione" onClick={(e) => e.stopPropagation()}>
        {d.nextTask ? (
          <div className="min-w-0">
            <div className="truncate text-[13px]">{d.nextTask.title}</div>
            <div className={`mono text-[12px] ${azione!.tone === 'alert' ? 'text-alert font-semibold' : azione!.tone === 'warn' ? 'text-warn' : 'mute'}`}>{azione!.label}</div>
          </div>
        ) : canWrite && d.isOpen ? (
          <div className="relative">
            <button className="btn btn-sm btn-ghost" onClick={() => setPlanning(true)}>
              <Icon name="calendarPlus" size={10} /> Programma
            </button>
            {planning && (
              <NextActionPopover
                company={azienda}
                pending={nextAction.isPending}
                onClose={() => setPlanning(false)}
                onConfirm={(v) => { setPlanning(false); nextAction.mutate({ id: d.id, ...v }); }}
              />
            )}
          </div>
        ) : <span className="mute-2">—</span>}
      </td>
      {/* Un avatar da solo, senza il nome accanto, sul telefono non dice niente:
          chi segue la trattativa si legge aprendola. */}
      <td data-m="off">{d.owner ? <Avatar initials={d.owner.initials} size={22} name={fullName(d.owner)} /> : <span className="mute-2">—</span>}</td>
      <td data-m="act" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end relative">
          {canWrite && (
            <ActionsMenu
              items={[
                { key: 'open', label: 'Apri', icon: 'arrowRight', onClick: () => onOpen(d.id) },
                { key: 'win', label: wonStage ? `Segna ${wonStage.name.toLowerCase()}` : 'Segna vinta', icon: 'check', hidden: !wonStage || d.isWon, onClick: () => win.mutate({ id: d.id, title: azienda, stageId: wonStage!.id, stageName: wonStage!.name, prevStageId: d.stageId }) },
                { key: 'move', label: 'Sposta in un\'altra pipeline', icon: 'swap', onClick: () => setMoving(true) },
                { key: 'close', label: 'Chiudi', icon: 'x', danger: true, hidden: d.isClosed || !lostStages.length, onClick: () => setClosing(true) },
              ]}
            />
          )}
          {closing && (
            <ClosePopover
              title={azienda}
              stages={lostStages}
              pending={close.isPending}
              onClose={() => setClosing(false)}
              onConfirm={(v) => { setClosing(false); close.mutate({ id: d.id, title: azienda, prevStageId: d.stageId, ...v }); }}
            />
          )}
          {moving && (
            <MovePipelinePopover
              title={azienda}
              currentPipelineId={d.pipelineId}
              pending={movePipeline.isPending}
              onClose={() => setMoving(false)}
              onConfirm={(v) => { setMoving(false); movePipeline.mutate({ id: d.id, title: azienda, ...v }); }}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

// ════════════════════════════════════════════════════════════════════
// Kanban: le colonne sono gli step della pipeline scelta, più "Chiuse".
// Trascinare in Chiuse chiede come e perché (le uscite restano distinte).
// ════════════════════════════════════════════════════════════════════
const CLOSED_COL = 'CHIUSE';

function DropZone({ dnd, col, position, visible }: {
  dnd: ReturnType<typeof useKanbanDnd<string>>;
  col: string;
  position: number;
  visible: boolean;
}) {
  if (!visible) return null;
  const active = dnd.isOverZone(col, position);
  return <div {...dnd.dropZoneProps(col, position)} className={`kanban-dropzone${active ? ' is-over' : ''}`} />;
}

function KanbanView({ pipeline, queryKey, isLoading, items, canWrite, hasFilters, onClearFilters, onOpen }: {
  pipeline: Pipeline;
  queryKey: readonly unknown[];
  isLoading: boolean;
  items: DealItem[];
  canWrite: boolean;
  hasFilters: boolean;
  onClearFilters: () => void;
  onOpen: (id: string) => void;
}) {
  const { kanbanMove } = useDealActions();
  // Spostamento in sospeso: la card è già stata trascinata in Chiuse, manca il motivo
  const [pendingClose, setPendingClose] = useState<{ deal: DealItem; position: number } | null>(null);

  const stages = pathStages(pipeline);
  const lostStages = exitStages(pipeline);
  const byId = useMemo(() => new Map(pipeline.stages.map((s) => [s.id, s])), [pipeline]);

  const columns = useMemo(() => {
    const map = new Map<string, DealItem[]>();
    for (const s of stages) map.set(s.id, []);
    map.set(CLOSED_COL, []);
    for (const d of items) map.get(d.isClosed ? CLOSED_COL : d.stageId)?.push(d);
    for (const [, arr] of map) arr.sort((a, b) => a.order - b.order);
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pipeline.id]);

  const orderFor = (col: string, position: number, skipId: string) => {
    const siblings = (columns.get(col) ?? []).filter((d) => d.id !== skipId);
    const p = Math.max(0, Math.min(position, siblings.length));
    if (siblings.length === 0) return 1000;
    if (p <= 0) return siblings[0]!.order - 1000;
    if (p >= siblings.length) return siblings[siblings.length - 1]!.order + 1000;
    return (siblings[p - 1]!.order + siblings[p]!.order) / 2;
  };

  const dnd = useKanbanDnd<string>((dealId, col, position) => {
    if (!canWrite) return;
    const deal = items.find((d) => d.id === dealId);
    if (!deal) return;
    if (col === CLOSED_COL) {
      if (deal.isClosed) return; // già chiusa: l'ordine in questa colonna non conta
      setPendingClose({ deal, position });
      return;
    }
    const stage = byId.get(col);
    if (!stage) return;
    kanbanMove.mutate({
      id: dealId, stageId: stage.id, stageName: stage.name, outcome: stage.outcome,
      position, order: orderFor(col, position, dealId),
      prevStageId: deal.stageId, title: counterparty(deal), queryKey,
    });
  });

  if (isLoading) {
    return (
      <div className="kanban kanban--fit">
        {stages.map((s) => (
          <div className="kanban-col" key={s.id}>
            <div className="kanban-col-hd"><Skeleton size="sm" w={90} /><Skeleton size="sm" w={24} /></div>
            <div className="kanban-col-body"><Skeleton size="block" h={72} /><Skeleton size="block" h={72} /></div>
          </div>
        ))}
      </div>
    );
  }

  if (!items.length) {
    return hasFilters ? (
      <EmptyState brand title="Nessuna trattativa con questi filtri" description="Azzera i filtri per vedere le altre."
        action={<button className="btn" onClick={onClearFilters}>Azzera i filtri</button>} />
    ) : (
      <EmptyState brand title={`${pipeline.name}: bacheca vuota`} description="Le trattative compaiono qui appena ne crei una." />
    );
  }

  const anyDragging = dnd.isAnyDragging();
  const cols: string[] = [...stages.map((s) => s.id), CLOSED_COL];

  return (
    <>
      <div className="kanban kanban--fit">
        {cols.map((c) => {
          const cards = columns.get(c) ?? [];
          const isOver = dnd.isOverColumn(c);
          const closedCol = c === CLOSED_COL;
          const stage = byId.get(c);
          return (
            <div className={`kanban-col${isOver ? ' is-over' : ''}`} key={c} {...(canWrite ? dnd.columnProps(c) : {})}>
              <div className="kanban-col-hd">
                <div className="kanban-col-title">
                  <span className="dot" style={{ background: closedCol ? 'var(--mute-2)' : stage?.outcome === 'VINTO' ? 'var(--text)' : 'var(--accent)' }} />
                  {closedCol ? 'Chiuse' : stage?.name}
                </div>
                <span className="kanban-col-count">{cards.length}</span>
              </div>
              <div className="kanban-col-body">
                <DropZone dnd={dnd} col={c} position={0} visible={anyDragging} />
                {cards.map((d, idx) => (
                  <Fragment key={d.id}>
                    <Card d={d} draggable={canWrite} dnd={dnd} col={c} onOpen={onOpen} />
                    {!closedCol && <DropZone dnd={dnd} col={c} position={idx + 1} visible={anyDragging} />}
                  </Fragment>
                ))}
                {!cards.length && !anyDragging && (
                  <div className="text-center py-6 mute-2 text-[12px]">{closedCol ? 'Nessuna chiusa.' : 'Niente qui.'}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Chiudere non è un semplice spostamento: si sceglie come e si scrive perché */}
      {pendingClose && (
        <div className="fixed inset-0 z-[62] flex items-start justify-center pt-[18vh]">
          <div className="relative">
            <ClosePopover
              title={counterparty(pendingClose.deal)}
              stages={lostStages}
              pending={kanbanMove.isPending}
              onClose={() => setPendingClose(null)}
              onConfirm={(v) => {
                const { deal, position } = pendingClose;
                setPendingClose(null);
                kanbanMove.mutate({
                  id: deal.id, stageId: v.stageId, stageName: v.stageName, outcome: 'PERSO',
                  position, order: orderFor(CLOSED_COL, position, deal.id),
                  prevStageId: deal.stageId, title: counterparty(deal), lostReason: v.lostReason, queryKey,
                });
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}

function Card({ d, draggable, dnd, col, onOpen }: {
  d: DealItem;
  draggable: boolean;
  dnd: ReturnType<typeof useKanbanDnd<string>>;
  col: string;
  onOpen: (id: string) => void;
}) {
  const prevista = dueInfo(d.expectedCloseDate, !d.isOpen);
  const azione = d.nextTask ? dueInfo(d.nextTask.dueDate) : null;
  return (
    <div className="kanban-card cursor-grab active:cursor-grabbing" {...(draggable ? dnd.cardProps(d.id, col) : {})} onClick={() => onOpen(d.id)}>
      <div className="kanban-card-title flex items-start gap-2">
        <span className="min-w-0 flex-1 font-semibold">{counterparty(d)}</span>
        <FasciaDot fascia={d.fascia} />
      </div>
      {d.contactName && <div className="text-[12px] mute -mt-1 mb-1.5 truncate">{d.contactName}</div>}
      <div className="row between text-[12px]">
        <span className="mono font-semibold"><Valore d={d} /></span>
        <span className={`mono ${prevista.tone === 'alert' ? 'text-alert font-semibold' : prevista.tone === 'warn' ? 'text-warn' : 'mute'}`}>
          {d.expectedCloseDate ? fmtDate(d.expectedCloseDate) : '—'}
        </span>
      </div>
      <div className="row between mt-1.5">
        {/* Su una chiusa conta come si è chiusa, non i requisiti */}
        {d.isClosed
          ? <span className="t-micro mute-2">{d.stage.name}</span>
          : d.isVoucher ? <ReqDots d={d} /> : <span />}
        {d.owner && <Avatar initials={d.owner.initials} size={18} name={fullName(d.owner)} />}
      </div>
      {d.nextTask && (
        <div className={`text-[12px] mt-2 pt-2 border-t border-line flex items-center gap-1.5 ${azione!.tone === 'alert' ? 'text-alert' : 'mute'}`} title={d.nextTask.title}>
          <Icon name="calendarClock" size={10} className="shrink-0" />
          <span className="truncate min-w-0 flex-1">{d.nextTask.title}</span>
          <span className="mono shrink-0">{azione!.label}</span>
        </div>
      )}
      {d.lostReason && <div className="text-[12px] mute mt-2 truncate" title={d.lostReason}>{d.lostReason}</div>}
    </div>
  );
}

export type { StageLite };
